import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Page, alert } from 'equasion-lib';
import { FadeLoader } from 'react-spinners';
import { FiAlertTriangle, FiCheckCircle, FiEdit, FiList, FiX } from 'react-icons/fi';
import ActionTopBar from '@/lib/shared-coms/ActionTopBar';
import SharedScanner from '@/lib/shared-coms/SharedScanner';
import ZonePickerModal from '@/lib/shared-coms/ZonePickerModal';
import type { ChosenZone } from '@/lib/shared-coms/ZonePickerModal';
import { explainUnusableSerialScan } from '@/lib/scanning/explainScan';
import { playSuccessTone } from '@/lib/scanning/feedback';
import { scanAlert } from '@/lib/scanning/scanAlert';
import { formatDateOnly } from '@/lib/utils/dateUtils';
import { operatorFacingMessage } from '@/lib/utils/errorUtils';
import { SCAN_PROFILE } from '@/root/App/com/ScannerProvider/ScannerProvider';
import {
  MODE_COPY,
  addToList,
  addedNotice,
  clearDraft,
  alreadyInZoneNotice,
  assignItems,
  duplicateNotice,
  findScanned,
  itemLabel,
  itemsInZone,
  listLocationId,
  listRefusal,
  listTotals,
  loadDraft,
  plural,
  productLine,
  removeFromList,
  saveDraft,
  totalsLabel,
  typeLabel,
  type BulkListItem,
  type BulkScanDraft,
  type BulkScanInput,
  type BulkScanItem,
  type BulkScanMode
} from './bulkScanList';
import './BulkScan.scss';

export type BulkScanCheck = {
  accepted: boolean;
  refusal?: { code: string; message: string } | null;
  item?: BulkScanItem | null;
};

export type BulkScanAssignResult = {
  itemCount: number;
  unitCount: number;
  zoneId: string;
  zoneName: string;
};

type BulkScanProps = {
  mode: BulkScanMode;
  companyId: string;
  /** `bulkScanCheck`. `error` is set when the request itself failed. */
  checkScan: (
    scan: BulkScanInput,
    listedSerializedItemIds: string[]
  ) => Promise<{ check: BulkScanCheck | null; error?: any }>;
  /** `bulkScanAssign`. Throws on refusal — the server writes nothing then. */
  assign: (zoneId: string, items: ReturnType<typeof assignItems>) => Promise<BulkScanAssignResult>;
  /** The item a Scanned Options screen sent here, scanned for the operator as the screen opens. */
  initialScan?: BulkScanInput | null;
  /** Where the list is kept between visits (Zone Count's draft pattern). Null: not kept. */
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
};

// equasion's ButtonProps type omits onClick (it reaches the <button> through ...rest at runtime);
// a spread is not excess-property checked.
const click = (onClick: () => void) => ({ onClick });

const defaultStorage = (): Storage | null => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
};


const timeLabel = (at: number) =>
  new Date(at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

type ScanLike = {
  serialNumber?: string | null;
  gtin?: string | null;
  lot?: string | null;
  expiration?: string | null;
  isManualEntry?: boolean;
};

/**
 * Bulk Stock / Bulk Move: scan every unit, case or pallet going into one zone — the list builds as
 * they are scanned, with a running count — then scan that zone once and stock (or move) the lot in
 * one action. The single-item flow (scan → Stock / Move → zone) is untouched; this is the same
 * write, repeated, with the zone asked for once.
 *
 * Each scan is checked by the server before it joins the list (status, duplicates, packaging
 * overlap — a case replaces listed units inside it), so the list only ever holds what can go.
 * Nothing is written until the zone is assigned, and then all or nothing. Until then the list is
 * kept on this device, as Zone Count keeps a count: leaving by any route loses nothing, and the
 * next visit offers it back.
 */
function BulkScan({
  mode,
  companyId,
  checkScan,
  assign,
  initialScan = null,
  storage = defaultStorage()
}: BulkScanProps) {
  const navigate = useNavigate();
  const copy = MODE_COPY[mode];
  const [list, setList] = useState<BulkListItem[]>([]);
  const [zone, setZone] = useState<ChosenZone | null>(null);
  const [zonePickerOpen, setZonePickerOpen] = useState(false);
  const [conflictIds, setConflictIds] = useState<string[]>([]);
  const [confirm, setConfirm] = useState<'clear' | 'leave' | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<BulkScanAssignResult | null>(null);
  const [resumeOffer, setResumeOffer] = useState<BulkScanDraft | null>(() =>
    storage && companyId ? loadDraft(storage, companyId, mode, Date.now()) : null
  );

  // Scans are handled one at a time, in the order they arrive: each check is made against the list
  // as the previous scan left it. A handheld can fire faster than a check returns.
  const listRef = useRef(list);
  const zoneRef = useRef(zone);
  // Set once the assign has taken its snapshot of the list, until the list is started over (or the
  // assign fails): a scan finishing after that point would sit on a list that was never sent.
  const busyRef = useRef(false);
  const queueRef = useRef<Promise<void>>(Promise.resolve());
  const resumePendingRef = useRef(!!resumeOffer);
  zoneRef.current = zone;

  const commitList = (next: BulkListItem[]) => {
    listRef.current = next;
    setList(next);
  };

  // While the list is being assigned, or its result is on screen, a scan cannot join it: say so
  // rather than drop it silently — the operator may already be scanning the next batch.
  const refuseWhileBusy = () => {
    if (!busyRef.current) return false;
    scanAlert(
      'Not added',
      'This list was already sent. Tap Scan more to start a new list, then scan it again.',
      6,
      'warning'
    );
    return true;
  };

  const processScan = async (raw: ScanLike) => {
    const serialNumber = raw?.serialNumber?.trim();
    if (!serialNumber) {
      const refusal = explainUnusableSerialScan(raw as any);
      if (refusal && !raw?.isManualEntry) scanAlert(refusal.title, refusal.message, 6, 'warning');
      return;
    }
    if (refuseWhileBusy()) return;
    if (resumePendingRef.current) {
      scanAlert(
        'Not added',
        'Choose Resume list or Start over first, then scan it again.',
        6,
        'warning'
      );
      return;
    }
    const scan: BulkScanInput = {
      serialNumber,
      gtin: raw.gtin?.trim() || null,
      lot: raw.lot || null,
      expiration: raw.expiration || null
    };
    const repeat = findScanned(listRef.current, scan);
    if (repeat) {
      const notice = duplicateNotice(repeat);
      scanAlert(notice.title, notice.message, 4, 'warning');
      return;
    }

    setChecking(true);
    let response: Awaited<ReturnType<typeof checkScan>>;
    try {
      response = await checkScan(
        scan,
        listRef.current.map((item) => item.serializedItemId)
      );
    } catch {
      response = { check: null, error: {} };
    } finally {
      setChecking(false);
    }
    // The assign may have started while this scan was being checked.
    if (refuseWhileBusy()) return;
    const { check, error } = response;
    if (error || !check) {
      // GraphQL errors already toast from the Apollo error link. A dropped connection does not.
      if (!error?.graphQLErrors?.length) {
        scanAlert('Could not check that scan', 'Check your connection and scan it again.', 6, 'warning');
      }
      return;
    }
    if (!check.accepted || !check.item) {
      scanAlert('Not added', check.refusal?.message || 'That item cannot be added.', 6, 'warning');
      return;
    }
    const item = check.item;
    const refusal = listRefusal(listRef.current, item);
    if (refusal) {
      scanAlert(refusal.title, refusal.message, 6, 'warning');
      return;
    }
    const chosenZone = zoneRef.current;
    if (mode === 'MOVE' && chosenZone && item.zoneId === chosenZone.id) {
      scanAlert(
        'Not added',
        `${itemLabel(item)} is already in ${chosenZone.display}, the zone you are moving to.`,
        6,
        'warning'
      );
      return;
    }

    const { list: next, absorbed } = addToList(listRef.current, item, scan);
    commitList(next);
    playSuccessTone();
    const notice = addedNotice(item, absorbed);
    if (notice) alert(notice.title, notice.message, 5, 'info');
  };

  const handleScan = (raw: ScanLike) => {
    queueRef.current = queueRef.current.then(() => processScan(raw)).catch(() => {});
  };

  // The item the screen was opened with — once the resume question, if any, is settled.
  const initialScanRef = useRef(initialScan);
  const scanInitial = () => {
    const pending = initialScanRef.current;
    initialScanRef.current = null;
    if (pending?.serialNumber) handleScan(pending);
  };

  useEffect(() => {
    if (!resumePendingRef.current) scanInitial();
    // The screen's first render only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const settleResume = (draft: BulkScanDraft | null) => {
    resumePendingRef.current = false;
    setResumeOffer(null);
    if (draft) commitList(draft.list);
    else if (storage) clearDraft(storage, companyId, mode);
    scanInitial();
  };

  // The list is kept on this device as it grows, so a reload or a wrong turn loses nothing. Saved
  // at once, not debounced: it changes once per scan, and leaving right after a scan must keep it.
  useEffect(() => {
    if (!storage || resumeOffer || result) return;
    if (list.length) saveDraft(storage, companyId, mode, list, Date.now());
    else clearDraft(storage, companyId, mode);
  }, [list, storage, companyId, mode, resumeOffer, result]);

  const remove = (serializedItemId: string) => {
    const next = removeFromList(listRef.current, serializedItemId);
    // An emptied list starts over: its zone was chosen for the location the removed items were at.
    if (!next.length) {
      clearList();
      return;
    }
    commitList(next);
    setConflictIds((ids) => ids.filter((id) => id !== serializedItemId));
  };

  const clearList = () => {
    commitList([]);
    setZone(null);
    setConflictIds([]);
  };

  /** False — and the operator told which items — when the destination holds listed items. */
  const zoneAccepts = (candidate: ChosenZone) => {
    if (mode !== 'MOVE') return true;
    const inZone = itemsInZone(listRef.current, candidate.id);
    setConflictIds(inZone.map((item) => item.serializedItemId));
    if (!inZone.length) return true;
    const notice = alreadyInZoneNotice(inZone, candidate.display);
    scanAlert(notice.title, notice.message, 10, 'warning');
    return false;
  };

  const submit = async () => {
    if (!zone || !list.length || submitting || busyRef.current) return;
    setSubmitting(true);
    // Scans still being checked land on the list first, so the assign sends what the operator saw.
    let pending: Promise<void>;
    do {
      pending = queueRef.current;
      await pending;
    } while (pending !== queueRef.current);
    if (!listRef.current.length || !zoneAccepts(zone)) {
      setSubmitting(false);
      return;
    }
    busyRef.current = true;
    try {
      const assigned = await assign(zone.id, assignItems(listRef.current));
      if (storage) clearDraft(storage, companyId, mode);
      setResult(assigned);
      playSuccessTone();
    } catch (e) {
      busyRef.current = false;
      // The server names the items it refused; nothing was written.
      scanAlert(
        `Not ${copy.past.toLowerCase()}`,
        operatorFacingMessage(e, `Nothing was ${copy.past.toLowerCase()}. Try again.`),
        12
      );
    }
    setSubmitting(false);
  };

  const startOver = () => {
    clearList();
    setResult(null);
    busyRef.current = false;
  };

  const { itemCount, unitCount } = listTotals(list);
  const countLabel = `${plural(itemCount, 'item')} (${plural(unitCount, 'unit')})`;

  return (
    <Page className={`bulk-scan bulk-scan--${mode.toLowerCase()} action-page`}>
      <ActionTopBar
        action={copy.title}
        actionColor={mode === 'STOCK' ? 'var(--b600)' : undefined}
        onBack={() => (list.length ? setConfirm('leave') : navigate('/'))}
      />
      <div className="action-page-body">
        <div className="bulk-scan__scanner">
          <SharedScanner showSSCC scanProfile={SCAN_PROFILE.ITEM} handleScan={handleScan} />
        </div>

        <div className="bulk-scan__summary" role="status">
          <div>
            <strong className="bulk-scan__count">{totalsLabel(list)}</strong>
            {checking && <span className="bulk-scan__checking">Checking…</span>}
          </div>
          {!!list.length && (
            <Button type="secondary" size="compact" {...click(() => setConfirm('clear'))}>
              Clear list
            </Button>
          )}
        </div>

        {list.length ? (
          <ul className="bulk-scan__list">
            {list.map((item) => {
              const product = productLine(item);
              const expiration = formatDateOnly(item.expiration);
              const meta = [
                item.lot ? `Lot ${item.lot}` : null,
                expiration ? `Exp ${expiration}` : null,
                mode === 'MOVE' && item.zoneName ? `In ${item.zoneName}` : null,
                mode === 'STOCK' && item.purchaseOrderNumber ? `PO # ${item.purchaseOrderNumber}` : null
              ].filter(Boolean);
              const conflict = conflictIds.includes(item.serializedItemId);
              return (
                <li
                  key={item.serializedItemId}
                  className={['bulk-scan__row', conflict && 'bulk-scan__row--conflict']
                    .filter(Boolean)
                    .join(' ')}
                >
                  <div className="bulk-scan__row-main">
                    <div className="bulk-scan__row-title">
                      <span className="bulk-scan__type">{typeLabel(item.serializedItemType)}</span>
                      <span className="bulk-scan__serial">{item.serialNumber}</span>
                    </div>
                    {product && <div className="bulk-scan__row-product">{product}</div>}
                    {!!meta.length && <div className="bulk-scan__row-meta">{meta.join(' · ')}</div>}
                    {conflict && zone && (
                      <div className="bulk-scan__row-conflict">Already in {zone.display}</div>
                    )}
                  </div>
                  <div className="bulk-scan__row-count">
                    <b>{item.unitCount}</b>
                    <small>{item.unitCount === 1 ? 'unit' : 'units'}</small>
                  </div>
                  <button
                    type="button"
                    className="bulk-scan__remove"
                    aria-label={`Remove ${itemLabel(item)}`}
                    onClick={() => remove(item.serializedItemId)}
                  >
                    <FiX aria-hidden />
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="bulk-scan__empty">
            Scan each unit, case or pallet {mode === 'STOCK' ? 'going into' : 'moving to'} one
            zone. Scan the zone once at the end.
          </p>
        )}

        <div className="bulk-scan__footer">
          {zone && (
            <div className="bulk-scan__zone" onClick={() => setZonePickerOpen(true)}>
              <FiEdit />
              <h1>{mode === 'STOCK' ? 'Assigned Zone' : 'Destination Zone'}</h1>
              <h2>{zone.display}</h2>
            </div>
          )}
          {zone ? (
            <Button
              type="primary"
              fullWidth
              disabled={submitting || !list.length}
              {...click(submit)}
            >
              {copy.verb} {countLabel} {mode === 'STOCK' ? 'in' : 'to'} {zone.display}
            </Button>
          ) : (
            <Button
              type="primary"
              fullWidth
              disabled={!list.length}
              {...click(() => setZonePickerOpen(true))}
            >
              {list.length ? `Scan zone for ${countLabel}` : 'Scan items first'}
            </Button>
          )}
        </div>

        {submitting && (
          <div className="blocking-spinner-container" onClick={(e) => e.stopPropagation()}>
            <FadeLoader color="var(--n0)" />
          </div>
        )}
      </div>

      {/* One full-screen panel, as Quarantine Cleanup: the result, the resume question, or a
          confirmation. The safe choice is the primary button; discarding is the danger one. */}
      <div
        className={['result-panel', (result || resumeOffer || confirm) && 'open']
          .filter(Boolean)
          .join(' ')}
      >
        {result ? (
          <>
            <div className="result-message">
              <FiCheckCircle style={{ color: 'var(--green)' }} />
              <h1>{copy.past} Successfully</h1>
              <p>
                {plural(result.itemCount, 'item')} ({plural(result.unitCount, 'unit')}){' '}
                {copy.past.toLowerCase()} {mode === 'STOCK' ? 'in' : 'to'} zone{' '}
                <b>{result.zoneName}</b>
              </p>
            </div>
            <div className="result-actions">
              <Button type="primary" fullWidth {...click(() => navigate('/'))}>
                Done
              </Button>
              <Button type="secondary" fullWidth {...click(startOver)}>
                Scan more
              </Button>
            </div>
          </>
        ) : resumeOffer ? (
          <>
            <div className="result-message">
              <FiList style={{ color: 'var(--n0)' }} />
              <h1>Resume your {copy.title} list?</h1>
              <p>
                {plural(resumeOffer.list.length, 'item')} scanned on this device, last at{' '}
                {timeLabel(resumeOffer.updatedAt)}. Every item is checked again when you assign
                the zone.
              </p>
            </div>
            <div className="result-actions">
              <Button type="primary" fullWidth {...click(() => settleResume(resumeOffer))}>
                Resume list
              </Button>
              <Button type="secondary" fullWidth {...click(() => settleResume(null))}>
                Start over
              </Button>
            </div>
          </>
        ) : confirm ? (
          <>
            <div className="result-message">
              <FiAlertTriangle style={{ color: 'var(--orange)' }} />
              <h1>{confirm === 'leave' ? 'Leave the list?' : 'Clear the list?'}</h1>
              <p>
                Nothing has been {copy.past.toLowerCase()} yet.{' '}
                {confirm === 'leave'
                  ? `The ${plural(itemCount, 'item')} stay on this device — open ${copy.title} again to pick them up.`
                  : `The ${plural(itemCount, 'item')} will be dropped from the list.`}
              </p>
            </div>
            <div className="result-actions">
              <Button type="primary" fullWidth {...click(() => setConfirm(null))}>
                Keep scanning
              </Button>
              {confirm === 'leave' && (
                <Button type="secondary" fullWidth {...click(() => navigate('/'))}>
                  Leave
                </Button>
              )}
              <Button
                type="danger"
                fullWidth
                {...click(() => {
                  const leaving = confirm === 'leave';
                  clearList();
                  if (storage) clearDraft(storage, companyId, mode);
                  setConfirm(null);
                  if (leaving) navigate('/');
                })}
              >
                {confirm === 'leave' ? 'Clear list and leave' : 'Clear list'}
              </Button>
            </div>
          </>
        ) : null}
      </div>

      <ZonePickerModal
        className="bulk-scan-zone-modal"
        open={zonePickerOpen}
        onClose={() => setZonePickerOpen(false)}
        title={mode === 'STOCK' ? 'Select a zone' : 'Select a destination zone'}
        label={mode === 'STOCK' ? 'Zone' : 'Destination Zone'}
        companyId={companyId}
        locationId={listLocationId(list)}
        zoneTypes={['stock']}
        scanTitle={copy.zoneTitle}
        scanHint={`${totalsLabel(list)}. ${copy.zoneHint}`}
        onZoneChosen={(chosen) => {
          if (zoneAccepts(chosen)) setZone(chosen);
        }}
      />

    </Page>
  );
}

export default BulkScan;
